import { clean, parsePrice } from '../product';
import type { SiteFindings } from './types';

/**
 * Amazon ships **no JSON-LD and no OpenGraph** on a product page. Every
 * generic path therefore falls to the last resort — `<title>` and whatever
 * `<img>` tags exist — and the result is bad in three specific ways:
 *
 *   - `<title>` and `meta[name=description]` are byte-identical, both
 *     "Amazon.com: <product name> : <category>", so the description ends up
 *     being the title.
 *   - The real gallery is in an embedded script, not in `<img>` tags. The
 *     `<img>` tags carry `_AC_US40_` renditions — forty pixels wide.
 *   - The page furniture (store nav, sponsored banners) is full of large
 *     images that look like candidates.
 *
 * All three are fixed here by reading the places Amazon actually puts things.
 */

/** amazon.com, amazon.co.uk, amazon.de, … but not amazonaws.com. */
export function isAmazon(hostname: string): boolean {
  return /(^|\.)amazon\.(com|co\.uk|ca|de|fr|it|es|nl|se|pl|com\.au|com\.br|com\.mx|co\.jp|in|ae|sa|sg|com\.tr)$/i.test(
    hostname,
  );
}

/** No ISO code on the page, so it comes from the storefront. */
const CURRENCIES: Record<string, string> = {
  'amazon.com': 'USD',
  'amazon.co.uk': 'GBP',
  'amazon.ca': 'CAD',
  'amazon.de': 'EUR',
  'amazon.fr': 'EUR',
  'amazon.it': 'EUR',
  'amazon.es': 'EUR',
  'amazon.nl': 'EUR',
  'amazon.se': 'SEK',
  'amazon.pl': 'PLN',
  'amazon.com.au': 'AUD',
  'amazon.com.br': 'BRL',
  'amazon.com.mx': 'MXN',
  'amazon.co.jp': 'JPY',
  'amazon.in': 'INR',
};

export function readAmazon(doc: Document, hostname: string): SiteFindings {
  return {
    title: clean(doc.querySelector('#productTitle')?.textContent),
    description: aboutThisItem(doc),
    price: price(doc),
    currency: CURRENCIES[hostname.replace(/^www\./, '')],
    images: gallery(doc),
  };
}

/**
 * The "About this item" bullets — which is what the user means by the
 * description, and what the store actually wrote about the product. The page
 * title is a keyword-stuffed sentence, not a description.
 *
 * Falls back to the long-form "Product Description" block on listings that
 * have one instead.
 */
function aboutThisItem(doc: Document): string | undefined {
  const bullets = [...doc.querySelectorAll('#feature-bullets li')]
    .map((item) => clean(item.textContent))
    .filter((text): text is string => text !== undefined)
    // Amazon appends UI affordances into the same list on some listings.
    .filter((text) => !/^report an issue|^see more product details/i.test(text));

  if (bullets.length > 0) return bullets.join('\n');

  return clean(doc.querySelector('#productDescription')?.textContent);
}

/**
 * `.a-offscreen` is the screen-reader copy of the price — a single clean
 * "$25.30" rather than the split whole/fraction spans the page renders.
 */
function price(doc: Document): number | undefined {
  const holders = [
    '#corePrice_feature_div .a-offscreen',
    '#corePriceDisplay_desktop_feature_div .a-offscreen',
    '#priceblock_ourprice',
    '#priceblock_dealprice',
    '.a-price .a-offscreen',
  ];

  for (const selector of holders) {
    const found = parsePrice(doc.querySelector(selector)?.textContent);
    // Zero is what an unavailable listing renders; it isn't this thing's price.
    if (found !== undefined && found > 0) return found;
  }

  return undefined;
}

/**
 * The gallery the page shows top-left, which Amazon embeds as a JS object
 * rather than as markup:
 *
 *     'colorImages': { 'initial': [ { "hiRes": "…_AC_SL1500_.jpg", … }, … ] }
 *
 * `initial` is the currently-selected colourway. Other colours get their own
 * keys, and taking those too would import photos of a product you didn't buy —
 * so the array is read with a bracket scan rather than a loose regex over the
 * whole script.
 */
function gallery(doc: Document): string[] {
  for (const script of doc.querySelectorAll('script')) {
    const text = script.textContent;
    if (text === null || !text.includes("'colorImages'")) continue;

    const initial = initialArray(text);
    if (initial === undefined) continue;

    const urls = [...initial.matchAll(/"hiRes"\s*:\s*"(https:[^"]+)"/g)]
      .map((match) => match[1])
      .filter((url): url is string => url !== undefined);

    if (urls.length > 0) return [...new Set(urls)];
  }

  // Single-image listings have no colorImages block at all.
  const hires = clean(doc.querySelector('#landingImage')?.getAttribute('data-old-hires'));
  return hires === undefined ? [] : [hires];
}

/**
 * The text of the `initial` array, found by counting brackets. A non-greedy
 * regex stops at the first `}]`, which lands inside the nested `main` object
 * on every real listing.
 */
function initialArray(script: string): string | undefined {
  const key = script.indexOf("'initial'");
  if (key === -1) return undefined;

  const open = script.indexOf('[', key);
  if (open === -1) return undefined;

  let depth = 0;
  for (let at = open; at < script.length; at += 1) {
    const character = script[at];
    if (character === '[') depth += 1;
    else if (character === ']') {
      depth -= 1;
      if (depth === 0) return script.slice(open, at + 1);
    }
  }

  return undefined;
}
